import { useEffect, useState } from "react";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PHRASE = "SCHONE START";

export default function CleanStartDialog({ open, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [resetCounters, setResetCounters] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null); setConfirm(""); setResult(null); setResetCounters(true);
    api.get("/admin/clean-start/preview").then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clean-start", { confirm, reset_counters: resetCounters && preview?.counters_resettable });
      setResult(data);
      toast.success(`Schone start uitgevoerd: ${data.accounts_deleted} accounts verwijderd.`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opschonen mislukt."); }
    setBusy(false);
  };

  const row = (label, value, testid) => (
    <div className="flex justify-between gap-3 py-1 border-b border-border/60 text-sm" data-testid={testid}>
      <span>{label}</span><b className="font-mono-num">{value}</b>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none heavy-shadow" data-testid="clean-start-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight inline-flex items-center gap-2"><Sparkles className="w-5 h-5" /> Schone start</DialogTitle>
          <DialogDescription>
            Verwijdert alle testdata vóór livegang. Beheerders en hun eigen clubs blijven staan; accounts met betaalde facturen worden overgeslagen (bewaarplicht). Dit kan niet ongedaan worden gemaakt.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-1" data-testid="clean-start-result">
            {row("Accounts verwijderd (incl. clubs en scenario's)", result.accounts_deleted, "clean-start-result-accounts")}
            {row("Mollie-abonnementen opgezegd", result.subscriptions_canceled)}
            {row("Uitnodigingscodes", result.invites)}
            {row("Kortingscodes", result.discounts)}
            {row("Niet-betaalde testbestellingen", result.test_orders)}
            {row("Oude Stripe-testtransacties", result.payment_transactions)}
            {row("Inlogpogingen / reset-tokens", `${result.login_attempts} / ${result.reset_tokens}`)}
            {row("Wees-records van eerder verwijderde clubs", result.orphans)}
            {row("Tellers en logboek gereset", result.counters_reset ? "ja" : "nee", "clean-start-result-counters")}
            {result.accounts_skipped?.length > 0 && (
              <div className="mt-2 text-xs border-2 border-accent bg-accent/20 p-2" data-testid="clean-start-result-skipped">
                <b>Overgeslagen (betaalde facturen):</b> {result.accounts_skipped.join(", ")}
              </div>
            )}
            <div className="flex justify-end pt-3">
              <button onClick={onClose} data-testid="clean-start-close" className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Sluiten</button>
            </div>
          </div>
        ) : !preview ? (
          <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Tellen…</div>
        ) : (
          <div className="space-y-3">
            <div data-testid="clean-start-preview">
              {row("Accounts (met clubs, scenario's, teamleden)", preview.accounts, "clean-start-preview-accounts")}
              {row("Clubs van die accounts", preview.clubs)}
              {row("Uitnodigingscodes", preview.invites, "clean-start-preview-invites")}
              {row("Kortingscodes", preview.discounts)}
              {row("Niet-betaalde testbestellingen", preview.test_orders)}
              {row("Oude Stripe-testtransacties", preview.payment_transactions)}
              {row("Wees-records van eerder verwijderde clubs", preview.orphans)}
              {row("Logboek geplande taken", preview.cron_runs)}
            </div>
            {preview.accounts_paid_skipped.length > 0 && (
              <div className="text-xs border-2 border-accent bg-accent/20 p-2 flex gap-2" data-testid="clean-start-preview-skipped">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span><b>Blijven staan</b> ({preview.paid_orders} betaalde factuur/facturen): {preview.accounts_paid_skipped.join(", ")}. Tellers worden daarom niet gereset.</span>
              </div>
            )}
            <label className={`flex items-start gap-2 text-sm ${preview.counters_resettable ? "" : "opacity-50"}`}>
              <input type="checkbox" checked={resetCounters && preview.counters_resettable} disabled={!preview.counters_resettable}
                onChange={(e) => setResetCounters(e.target.checked)} data-testid="clean-start-reset-counters" className="mt-0.5 accent-foreground" />
              <span>Tellers resetten (factuurnummer vanaf MJP-{new Date().getFullYear()}-0001, klantnummer vanaf MJP-00001, AVG-nummering) en het logboek van geplande taken leegmaken</span>
            </label>
            <div>
              <label className="stat-label">Typ ter bevestiging: {PHRASE}</label>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder={PHRASE} data-testid="clean-start-confirm-input"
                className="w-full border-2 border-foreground bg-background px-3 py-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-destructive" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={busy} data-testid="clean-start-cancel" className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Annuleren</button>
              <button onClick={run} disabled={busy || confirm.trim() !== PHRASE} data-testid="clean-start-run"
                className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Alles verwijderen
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
